import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import http from '@/libs/http'
import { toErrorMessage } from '@/libs/errorToaster'
import type {
	AlertResponseEntry,
	AlertsApiResponse,
	CustomGraph,
	CustomGraphEquation,
} from '@/features/alerts/_types'

type LoadingKey =
	| 'fetchCustomGraphs'
	| 'fetchAlerts'
	| 'refreshAll'

type IndexEntry = {
	graph: CustomGraph
	equation: CustomGraphEquation
}

const makeKey = (value: unknown): string => {
	if (value === null || value === undefined) return ''
	return String(value)
}

const keyBy = <T extends Record<string, any>, K extends keyof T>(list: T[], prop: K): Record<string, T> => {
	const map: Record<string, T> = {}
	for (const item of list) {
		const key = makeKey(item[prop])
		if (!key) continue
		map[key] = item
	}
	return map
}

const groupBy = <T extends Record<string, any>, K extends keyof T>(list: T[], prop: K): Record<string, T[]> => {
	const map: Record<string, T[]> = {}
	for (const item of list) {
		const key = makeKey(item[prop] ?? '')
		if (!map[key]) map[key] = []
		map[key].push(item)
	}
	return map
}

export const useAlertsStore = defineStore('alerts', () => {
	const customGraphs = ref<CustomGraph[]>([])
	const alerts = ref<Record<string, AlertResponseEntry>>({})
	const warns = ref<Record<string, AlertResponseEntry>>({})

	const loadingMap = ref<Record<LoadingKey, boolean>>({
		fetchCustomGraphs: false,
		fetchAlerts: false,
		refreshAll: false,
	})
	const errorMap = ref<Record<LoadingKey, string>>({
		fetchCustomGraphs: '',
		fetchAlerts: '',
		refreshAll: '',
	})

	function setLoading(key: LoadingKey, value: boolean) {
		if (value) loadingMap.value[key] = true
		else delete loadingMap.value[key]
	}

	function setError(key: LoadingKey, message: string | null) {
		if (message) errorMap.value[key] = message
		else delete errorMap.value[key]
	}

	async function withLoading<T>(key: LoadingKey, fn: () => Promise<T>): Promise<T> {
		if (loadingMap.value[key]) {
			throw new Error(`${key} is already in progress (alerts store)`)
		}
		setLoading(key, true)
		setError(key, null)
		try {
			return await fn()
		} catch (error: any) {
			const message = toErrorMessage(error, 'Failed to process alert request')
			setError(key, message)
			throw new Error(message)
		} finally {
			setLoading(key, false)
		}
	}

	const customGraphsById = computed(() => keyBy(customGraphs.value, '_id'))
	const customGraphsBySerial = computed(() => keyBy(customGraphs.value, 'serial'))
	const customGraphsByProjectId = computed(() => groupBy(customGraphs.value, 'projectId'))
	const customGraphsByLocationId = computed(() => groupBy(customGraphs.value, 'locationId'))

	const equationIndex = computed<Record<string, IndexEntry>>(() => {
		const index: Record<string, IndexEntry> = {}
		for (const graph of customGraphs.value) {
			for (const equation of graph.equations || []) {
				const key = `${makeKey(graph.serial)}.${makeKey(equation.serial)}`
				index[key] = { graph, equation }
			}
		}
		return index
	})

	const hasLoadedGraphs = computed(() => customGraphs.value.length > 0)
	const hasLoadedAlerts = computed(() => Object.keys(alerts.value).length > 0 || Object.keys(warns.value).length > 0)

	const fetchCustomGraphs = async () =>
		withLoading('fetchCustomGraphs', async () => {
			const resp = await http.getAuth<CustomGraph[]>(`/api/v1/customgraphs/list`)
			customGraphs.value = resp || []
			return customGraphs.value
		})

	const fetchAlerts = async () =>
		withLoading('fetchAlerts', async () => {
			const resp = await http.getAuth<AlertsApiResponse>(`/api/v1/general/alerts`)
			alerts.value = resp?.alerts || {}
			warns.value = resp?.warns || {}
			return resp
		})

	const refreshAll = async () =>
		withLoading('refreshAll', async () => {
			await Promise.all([fetchCustomGraphs(), fetchAlerts()])
			return true
		})

	return {
		// state
		customGraphs,
		alerts,
		warns,
		loadingMap,
		errorMap,
		// computed helpers
		customGraphsById,
		customGraphsBySerial,
		customGraphsByProjectId,
		customGraphsByLocationId,
		equationIndex,
		hasLoadedGraphs,
		hasLoadedAlerts,
		// actions
		fetchCustomGraphs,
		fetchAlerts,
		refreshAll,
	}
})
